import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { addPdfPageNumberFooter } from "../../utils/pdf-page-footer";
import { TestReportResultDTO } from "../../services/TestReport/TestReport.service";

export interface PdfMeta {
  reportId: number | string;
  reportDate?: Date | string | null;
  manufacturingUnitName?: string | null;
  testedDate?: Date | string | null;
  analysisStart?: Date | string | null;
  analysisEnd?: Date | string | null;
  sampleQty?: number | string | null;
  // Optional helpers if you want to be explicit from service:
  sampleQtyUnit?: string | null; // e.g., "ml"
  sampleQtyText?: string | null; // e.g., "250 ml"
  testerName?: string | null;
  oilName?: string | null;
  batchName?: string | null;
  companyLines?: string[];
}

export type SampleGroup = {
  testId: number;
  testName: string;
  items: Array<{ label: string; value: string | number | null }>;
  chemicals?: string[];
  instruments?: string[];
};

type ToleranceMap = Map<number, { min: number | null; max: number | null }>;
type RGB = [number, number, number];
const BRAND_GREEN: RGB = [0, 102, 51];
const WATERMARK_OPACITY = 0.12;

const OIL_STRIP_FROM: RGB = [232, 248, 240];
const OIL_STRIP_TO: RGB = [204, 236, 221];

const MU_POS003_LINES = [
  "Behind Govt School,",
  "Adaganahalli Village,",
  "K R Nagar Taluk,",
  "Mysuru District,",
  "Karnataka – 571602,",
  "India",
];

const MU_POS004_LINES = [
  "Rajanapalya,",
  "Magadi Taluk,",
  "Ramanagara District,",
  "Karnataka – 562120,",
  "India",
];

const HCN_TEST_NAME = "HYDROCYANIC ACID VALUE";
const fmtDate = (d?: Date | string | null) =>
  d ? new Date(d).toLocaleDateString("en-GB") : "—";
const fmtTimeOnly = (d?: Date | string | null) =>
  d ? new Date(d).toLocaleTimeString() : "—";
const fmt4 = (n: number | string | null | undefined): string => {
  if (n == null) return "—";
  const num = Number(n);
  if (isNaN(num)) return String(n);
  return num.toFixed(4).replace(/\.?0+$/, ""); // 4 decimal places, remove trailing zeros
};

const loadImageAsDataURL = (
  src?: string,
  maxDim = 400,
  quality = 0.55
): Promise<string> =>
  new Promise((resolve) => {
    if (!src) return resolve("");
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
      const w = Math.max(1, Math.round(img.width * scale));
      const h = Math.max(1, Math.round(img.height * scale));
      const c = document.createElement("canvas");
      c.width = w;
      c.height = h;
      const ctx = c.getContext("2d");
      if (ctx) {
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, w, h);
        ctx.drawImage(img, 0, 0, w, h);
      }
      resolve(c.toDataURL("image/jpeg", quality));
    };
    img.onerror = () => resolve("");
    img.src = src;
  });

const norm = (s: string) => s.replace(/[^a-z0-9]/gi, "").toUpperCase();

function resolveCompanyLines(meta: PdfMeta): string[] {
  if (meta.companyLines?.length) return meta.companyLines;
  const n = norm((meta.manufacturingUnitName ?? "").toString());
  if (n.includes("KRNAGAR") || n.includes("KARNAGAR")) return MU_POS003_LINES;
  if (n.includes("MAGADI")) return MU_POS004_LINES;
  return [];
}

function normalizeStatus(status?: string | null): string {
  if (!status) return "—";
  const s = status.trim().toUpperCase();
  if (s === "PASS" || s === "FAIL") return s;
  return "Pending";
}

function gradientRect(
  doc: jsPDF,
  x: number,
  y: number,
  w: number,
  h: number,
  from: RGB,
  to: RGB,
  steps = 30
) {
  const [r1, g1, b1] = from,
    [r2, g2, b2] = to;
  const stepW = w / steps;
  for (let i = 0; i < steps; i++) {
    const t = i / (steps - 1);
    const r = Math.round(r1 + (r2 - r1) * t);
    const g = Math.round(g1 + (g2 - g1) * t);
    const b = Math.round(b1 + (b2 - b1) * t);
    doc.setFillColor(r, g, b);
    doc.rect(x + i * stepW, y, stepW + 0.5, h, "F");
  }
}

const isHCN = (name?: string) =>
  (name ?? "").trim().toUpperCase() === HCN_TEST_NAME;

const formatHcnYesNo = (v: unknown): string => {
  if (v === null || v === undefined || v === "") return "—";
  const n = Number(v);
  if (!Number.isFinite(n)) return String(v);
  return n >= 0.5 ? "Yes" : "No";
};

// Unit-aware display for Sample Quantity ("ml" by default)
const fmtSampleQtyDisplay = (meta: PdfMeta): string => {
  if (meta.sampleQtyText != null && String(meta.sampleQtyText).trim() !== "") {
    return String(meta.sampleQtyText).trim();
  }
  const v = meta.sampleQty;
  if (v == null) return "—";
  const raw = String(v).trim();
  if (raw === "") return "—";
  // If already contains letters (likely a unit), use as-is
  if (/[a-zA-Z]/.test(raw)) return raw;
  // Else append default or provided unit
  const unit = (meta.sampleQtyUnit ?? "ml").toString().trim();
  return `${raw} ${unit}`;
};

export async function buildSampleReportPdf(args: {
  meta: PdfMeta;
  groups: SampleGroup[];
  centerLogoUrl?: string;
  qaSealUrl?: string;
  results?: TestReportResultDTO[];
  tolerances?: ToleranceMap;
}): Promise<jsPDF> {
  const { meta, groups, centerLogoUrl, qaSealUrl, results = [], tolerances } = args;

  const resultByTestId = new Map<number, TestReportResultDTO>();
  if (results && Array.isArray(results)) {
    (results ?? []).forEach((r) => {
      if (r && typeof r === 'object' && 'testId' in r) {
        resultByTestId.set(r.testId, r);
      }
    });
  }

  const getRangeText = (testId: number) => {
    const tol = tolerances?.get(testId);
    if (!tol) return "—";
    const { min, max } = tol;
    if (min != null && max != null) return `${fmt4(min)} – ${fmt4(max)}`;
    if (min != null) return `≥ ${fmt4(min)}`;
    if (max != null) return `≤ ${fmt4(max)}`;
    return "—";
  };

  const logoData = await loadImageAsDataURL(centerLogoUrl, 400, 0.60);
  const sealData = await loadImageAsDataURL(qaSealUrl, 200, 0.60);

  const doc = new jsPDF({ orientation: "portrait", unit: "pt", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();

  const M = 18;
  const titleSize = 18;
  const logoW = 180;
  const logoH = 60;
  const addrFont = 9;
  const addrLineGap = 12;
  const metaFont = 9;
  const metaLineGap = 12;

  const CONTENT_L = M + 18;
  const CONTENT_R = pageW - (M + 18);
  const CONTENT_W = CONTENT_R - CONTENT_L;

  const watermarkedPages = new Set<number>();
  const drawWatermarkForCurrentPage = () => {
    if (!logoData) return;
    const info = (doc as any).getCurrentPageInfo?.();
    const pageNo = info?.pageNumber ?? doc.getNumberOfPages();
    if (watermarkedPages.has(pageNo)) return;

    if ((doc as any).GState && (doc as any).setGState) {
      const gs = new (doc as any).GState({ opacity: WATERMARK_OPACITY });
      (doc as any).setGState(gs);
    }

    const wmW = CONTENT_W * 0.75;
    const wmH = wmW * 0.55;
    const wmX = CONTENT_L + (CONTENT_W - wmW) / 2;
    const wmY = M + 260;
    doc.addImage(logoData, "JPEG", wmX, wmY, wmW, wmH);

    if ((doc as any).GState && (doc as any).setGState) {
      const gsNormal = new (doc as any).GState({ opacity: 1 });
      (doc as any).setGState(gsNormal);
    }
    watermarkedPages.add(pageNo);
  };

  doc.setDrawColor(180).setLineWidth(0.8);
  doc.rect(M, M, pageW - 2 * M, pageH - 2 * M);

  const HEADER_H = 85;
  const headerY = M;

  const LOGO_MAX_W = 130,
    LOGO_MAX_H = 50;
  if (logoData) {
    const w = Math.min(LOGO_MAX_W, logoW);
    const h = Math.min(LOGO_MAX_H, logoH);
    doc.addImage(logoData, "JPEG", CONTENT_L, headerY + (HEADER_H - h) / 2, w, h);
  }

  doc.setFont("helvetica", "bold").setTextColor(0).setFontSize(titleSize + 3);
  doc.text("SAMPLE REPORT", pageW / 2, headerY + HEADER_H / 2 + 6, {
    align: "center",
  });

  if (sealData) {
    const QA_SEAL_W = 90;
    const QA_SEAL_H = 75;
    const sealX = CONTENT_R - QA_SEAL_W;
    doc.addImage(sealData, "JPEG", sealX, headerY + 4, QA_SEAL_W, QA_SEAL_H);
  }

  const metaTop = headerY + HEADER_H + 40;
  const COL_GAP = 14;

  const LEFT_W = Math.min(CONTENT_W * 0.4, 180);
  const RIGHT_W = CONTENT_W - LEFT_W - COL_GAP;
  const rightX = CONTENT_L + LEFT_W + COL_GAP;

  const companyLines = resolveCompanyLines(meta);
  doc.setFontSize(addrFont + 0.5).setFont("helvetica", "bold").setTextColor(25);
  doc.text("Address:", CONTENT_L, metaTop);
  doc.setFont("helvetica", "normal").setTextColor(55);
  let yL = metaTop + 12;
  for (const ln of companyLines) {
    doc.text(ln, CONTENT_L, yL, { maxWidth: LEFT_W });
    yL += addrLineGap;
  }
  const leftBlockBottom = yL;

  const innerGap = 30;
  const innerColW = (RIGHT_W - innerGap) / 2;
  const col1: Array<[string, string]> = [
    ["Report No", String(meta.reportId ?? "—")],
    ["Report Date", fmtDate(meta.reportDate)],
    ["Analysis Start", fmtTimeOnly(meta.analysisStart)],
    ["Analysis End", fmtTimeOnly(meta.analysisEnd)],
  ];
  const col2: Array<[string, string]> = [
    ["Manufacturing Unit", String(meta.manufacturingUnitName ?? "—")],
    ["Tested By", meta.testerName ?? "—"],
    ["Tested Date", fmtDate(meta.testedDate)],
    ["Sample Qty", fmtSampleQtyDisplay(meta)], // <- unit-aware
  ];

  doc.setFontSize(metaFont);
  const LABEL_PAD = 8;
  const MAX_LW1 = 110;
  const MAX_LW2 = 100;

  let yRow = metaTop;
  for (let i = 0; i < 4; i++) {
    const [k1, v1] = col1[i];
    const k1Text = `${k1}:`;
    const k1Width = Math.min(MAX_LW1, doc.getTextWidth(k1Text) + LABEL_PAD);
    doc.setFont("helvetica", "bold").setTextColor(30);
    doc.text(k1Text, rightX, yRow);
    doc.setFont("helvetica", "normal").setTextColor(70);
    doc.text(v1, rightX + k1Width, yRow, { maxWidth: innerColW - k1Width });

    const [k2, v2] = col2[i];
    const x2 = rightX + innerColW + innerGap;
    const k2Text = `${k2}:`;
    const k2Width = Math.min(MAX_LW2, doc.getTextWidth(k2Text) + LABEL_PAD);
    doc.setFont("helvetica", "bold").setTextColor(30);
    doc.text(k2Text, x2, yRow);
    doc.setFont("helvetica", "normal").setTextColor(70);
    doc.text(v2, x2 + k2Width, yRow, { maxWidth: innerColW - k2Width });

    yRow += metaLineGap;
  }

  const metaBottom = Math.max(leftBlockBottom, yRow) + 6;
  const bandY = metaBottom + 8;
  const bandH = 26;

  doc.setDrawColor(150).setLineWidth(0.8);
  doc.line(CONTENT_L, bandY, CONTENT_R, bandY);
  doc.line(CONTENT_L, bandY + bandH, CONTENT_R, bandY + bandH);

  const STRIP_INSET = 1.2;
  gradientRect(
    doc,
    CONTENT_L,
    bandY + STRIP_INSET,
    CONTENT_W,
    bandH - STRIP_INSET * 2,
    OIL_STRIP_FROM,
    OIL_STRIP_TO,
    70
  );

  const LABEL_COLOR: RGB = [0, 90, 45];
  const VALUE_COLOR: RGB = [0, 0, 0];
  const leftPad = 10;
  const centerPad = 155;
  const oilLabel = "Oil Type : ";
  const batchLabel = "Batch No : ";
  const oilVal = String(meta.oilName ?? "—");
  const batchVal = String(meta.batchName ?? "—");
  const textY = bandY + bandH / 2 + 3;

  doc.setFontSize(10.5).setFont("helvetica", "bold").setTextColor(...LABEL_COLOR);
  const labelW = doc.getTextWidth(oilLabel);
  doc.text(oilLabel, CONTENT_L + leftPad, textY);

  doc.setFont("helvetica", "normal").setTextColor(...VALUE_COLOR);
  const maxOilW =
    CONTENT_W -
    leftPad -
    labelW -
    centerPad -
    doc.getTextWidth(batchLabel + batchVal) -
    6;

  let oilText = oilVal;
  if (doc.getTextWidth(oilText) > maxOilW && maxOilW > 20) {
    while (oilText.length > 1 && doc.getTextWidth(oilText + "…") > maxOilW) {
      oilText = oilText.slice(0, -1);
    }
    oilText += "…";
  }
  doc.text(oilText, CONTENT_L + leftPad + labelW, textY);

  doc.setFont("helvetica", "bold").setTextColor(...LABEL_COLOR);
  const batchX =
    CONTENT_L + leftPad + labelW + doc.getTextWidth(oilText) + centerPad;
  doc.text(batchLabel, batchX, textY);
  doc.setFont("helvetica", "normal").setTextColor(...VALUE_COLOR);
  doc.text(batchVal, batchX + doc.getTextWidth(batchLabel), textY);

  let cursorY = bandY + bandH + 32;

  const SECTION_TITLE_H = 16;     
  const KV_LINE_H = 14;           
  const GAP_BEFORE_TABLE = 6;

  const TABLE_CELL_PAD = 7;
  const TABLE_ROW_MIN = 20;
  const TABLE_HEAD_H = 24;        
  const SECTION_GAP = 30;
  const FOOTER_SPACE = 30;

  const roomLeft = () => pageH - (FOOTER_SPACE + 40) - cursorY;

  const ensureNewPageIfNeeded = (needed: number) => {
    if (roomLeft() < needed) {
      doc.addPage();
      drawWatermarkForCurrentPage();
      cursorY = M + 16;
    }
  };

  drawWatermarkForCurrentPage();

  groups.forEach((g, idx) => {
    const chemsText = (g.chemicals ?? []).join(", ").trim();
    const instrText = (g.instruments ?? []).join(", ").trim();
    const hasChems = chemsText.length > 0;
    const hasInstr = instrText.length > 0;
    const kvLinesCount = (hasChems ? 1 : 0) + (hasInstr ? 1 : 0);
    const kvBlockMinH = kvLinesCount * KV_LINE_H;
    const gapBeforeTableDynamic = kvLinesCount > 0 ? GAP_BEFORE_TABLE : 0;
    const minTableH = TABLE_HEAD_H + Math.max(1, g.items.length) * TABLE_ROW_MIN;
    const minSectionH =
      SECTION_TITLE_H + kvBlockMinH + gapBeforeTableDynamic + minTableH + SECTION_GAP;

    ensureNewPageIfNeeded(minSectionH);

    const sectionTitle = `${idx + 1}. ${g.testName}`;
    doc.setFont("helvetica", "bold").setFontSize(12).setTextColor(20);
    doc.text(sectionTitle, CONTENT_L, cursorY);
    cursorY += (SECTION_TITLE_H - 6);

    const labelFontSize = 10;
    const valueFontSize = 10;
    const LABELS = ["Chemicals Used:", "Instrument Used:"];
    doc.setFontSize(labelFontSize).setFont("helvetica", "bold");
    const maxLabelW = Math.max(...LABELS.map((L) => doc.getTextWidth(L)));
    const GAP_AFTER_LABEL = 8;
    const valueX = CONTENT_L + maxLabelW + GAP_AFTER_LABEL;

    const drawKV = (label: string, value: string) => {
      const yBase = cursorY + KV_LINE_H;
      doc.setFontSize(labelFontSize).setFont("helvetica", "bold").setTextColor(0, 90, 45);
      doc.text(label, CONTENT_L, yBase);
      doc.setFontSize(valueFontSize).setFont("helvetica", "normal").setTextColor(0);
      doc.text(doc.splitTextToSize(value, CONTENT_R - valueX) as string[], valueX, yBase);
      cursorY += KV_LINE_H;
    };

    if (hasChems) drawKV("Chemicals Used:", chemsText);
    if (hasInstr) drawKV("Instrument Used:", instrText);

    cursorY += gapBeforeTableDynamic;

    const r = resultByTestId.get(g.testId);
    const isAlmondUnsap =
      (meta.oilName ?? "").toUpperCase().includes("ALMOND") &&
      (g.testName ?? "").trim().toUpperCase() === "UNSAPONIFICATION VALUE";

    const isHcnTest = isHCN(g.testName);

    const resultText = isHcnTest
      ? (r?.result != null ? formatHcnYesNo(r.result) : "—")
      : r?.result != null
      ? fmt4(r.result)
      : "—";

    const tolText = isAlmondUnsap ? "N.S." : getRangeText(g.testId);
    const statusText = isAlmondUnsap ? "" : normalizeStatus(r?.status);

    const usableW = CONTENT_W;

    autoTable(doc, {
      startY: cursorY,
      theme: "grid",
      head: [["Sample Name", "Value", "Result", "FSSAI Tolerance", "Status"]],
      body: g.items.map((it) => [
        String(it.label || "—"),
        isHcnTest ? formatHcnYesNo(it.value) : it.value == null ? "—" : String(it.value),
        resultText,
        tolText,
        statusText,
      ]),
      pageBreak: "avoid",
      rowPageBreak: "avoid",
      showHead: "firstPage",
      showFoot: "never",
      styles: {
        fontSize: 10,
        cellPadding: 7,
        minCellHeight: 20,
        valign: "middle",
      },
      headStyles: {
        fillColor: BRAND_GREEN,
        textColor: 255,
        fontStyle: "bold",
        fontSize: 10.5,
      },
      margin: { left: CONTENT_L, right: pageW - CONTENT_R, bottom: FOOTER_SPACE },
      columnStyles: {
        0: { cellWidth: 0.40 * usableW, halign: "left" },   // Sample Name
        1: { cellWidth: 0.15 * usableW, halign: "right" },  // Value
        2: { cellWidth: 0.15 * usableW, halign: "right" },  // Result
        3: { cellWidth: 0.20 * usableW, halign: "center" }, // FSSAI Tolerance
        4: { cellWidth: 0.10 * usableW, halign: "center" }, // Status
      },
      tableLineColor: 185,
      tableLineWidth: 0.8,

      didDrawCell: (data: any) => {
        if (data.section === "body" && data.column.index === 4) {
          const val = String(data.cell.raw || "").toUpperCase();
          if (!val) return;
          const { x, y, width, height } = data.cell;
          const padX = 8;
          const textW = doc.getTextWidth(val);
          const pillW = Math.min(width - 6, textW + padX * 2);
          const pillH = Math.min(height - 6, 20);
          const px = x + (width - pillW) / 2;
          const py = y + (height - pillH) / 2;

          if (val === "PASS") doc.setFillColor(220, 245, 225);
          else if (val === "FAIL") doc.setFillColor(253, 225, 225);
          else doc.setFillColor(235, 235, 235);

          (doc as any).roundedRect(px, py, pillW, pillH, 7, 7, "F");
          doc.setFont("helvetica", "bold");
          if (val === "FAIL") doc.setTextColor(170, 0, 0);
          else doc.setTextColor(0, 120, 0);
          doc.text(val, px + pillW / 2, py + pillH / 2 + 3, { align: "center" });
          doc.setTextColor(0);
        }
      },

      didDrawPage: () => {
        drawWatermarkForCurrentPage();
      },
    });

    cursorY = (doc as any).lastAutoTable.finalY + SECTION_GAP;
  });

  addPdfPageNumberFooter(doc);
  return doc;
}

export async function generateSampleReportPdf(args: {
  meta: PdfMeta;
  groups: SampleGroup[];
  centerLogoUrl?: string;
  qaSealUrl?: string;
  filename?: string;
  results?: TestReportResultDTO[];
  tolerances?: ToleranceMap;
}) {
  const doc = await buildSampleReportPdf(args);
  doc.save(args.filename ?? `SampleReport_${args.meta.reportId}.pdf`);
}
